import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CalendarIcon, 
  EyeIcon, 
  ClockIcon, 
  TagIcon,
  MagnifyingGlassIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const NewsPage = () => {
  const [news, setNews] = useState([]);
  const [featuredNews, setFeaturedNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSport, setSelectedSport] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalArticles, setTotalArticles] = useState(0);

  // Sample news data (will be replaced by API data when available)
  const sampleNews = [
    {
      id: 1,
      title: 'Eagles Football Team Wins Championship Game',
      slug: 'eagles-football-championship-win',
      excerpt: 'In a thrilling overtime victory, the East End Prep Eagles defeated the Central Valley Hawks 28-21 to claim the regional championship title.',
      content: 'Full article content here...',
      images: ['/api/placeholder/600/400'],
      author: {
        id: 1,
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@eastendprep.edu'
      },
      category: 'sports',
      tags: ['football', 'championship', 'victory'],
      sport: {
        id: 1,
        name: 'Football',
        slug: 'football'
      },
      isPublished: true,
      isFeatured: true,
      viewCount: 245,
      publishedAt: '2024-01-15T10:00:00Z',
      createdAt: '2024-01-15T09:00:00Z',
      readingTime: 3
    },
    {
      id: 2,
      title: 'New Athletic Director Joins East End Prep',
      slug: 'new-athletic-director-announcement',
      excerpt: 'We are excited to welcome Coach Michael Thompson as our new Athletic Director, bringing 15 years of experience in middle school athletics.',
      content: 'Full article content here...',
      images: ['/api/placeholder/600/400'],
      author: {
        id: 2,
        firstName: 'Principal',
        lastName: 'Davis',
        email: 'principal@eastendprep.edu'
      },
      category: 'announcements',
      tags: ['staff', 'athletics', 'announcement'],
      sport: null,
      isPublished: true,
      isFeatured: false,
      viewCount: 128,
      publishedAt: '2024-01-12T14:30:00Z',
      createdAt: '2024-01-12T13:30:00Z',
      readingTime: 2
    },
    {
      id: 3,
      title: 'Girls Basketball Team Advances to State Tournament',
      slug: 'girls-basketball-state-tournament',
      excerpt: 'The Lady Eagles secured their spot in the state tournament with a dominant 65-42 victory over the Riverside Rams in the regional finals.',
      content: 'Full article content here...',
      images: ['/api/placeholder/600/400'],
      author: {
        id: 1,
        firstName: 'Sarah',
        lastName: 'Johnson',
        email: 'sarah.johnson@eastendprep.edu'
      },
      category: 'sports',
      tags: ['basketball', 'girls', 'tournament'],
      sport: {
        id: 4,
        name: 'Girls Basketball',
        slug: 'girls-basketball'
      },
      isPublished: true,
      isFeatured: true,
      viewCount: 189,
      publishedAt: '2024-01-10T16:00:00Z',
      createdAt: '2024-01-10T15:00:00Z',
      readingTime: 4
    }
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'sports', label: 'Sports' },
    { value: 'announcements', label: 'Announcements' },
    { value: 'events', label: 'Events' },
    { value: 'academics', label: 'Academics' }
  ];

  const sports = [
    { value: 'all', label: 'All Sports' },
    { value: 'football', label: 'Football' },
    { value: 'girls-basketball', label: 'Girls Basketball' },
    { value: 'boys-basketball', label: 'Boys Basketball' },
    { value: 'girls-soccer', label: 'Girls Soccer' },
    { value: 'boys-soccer', label: 'Boys Soccer' }
  ];

  useEffect(() => {
    fetchNews();
    fetchFeaturedNews();
  }, [currentPage, searchTerm, selectedCategory, selectedSport]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 9,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(selectedSport !== 'all' && { sport: selectedSport })
      });

      const response = await fetch(`${API_BASE_URL}/news?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setNews(data.articles || []);
        setTotalPages(data.pagination?.totalPages || 1);
        setTotalArticles(data.pagination?.totalItems || 0);
      } else {
        // Use sample data if API is not available
        const filteredNews = sampleNews.filter(article => {
          const matchesSearch = !searchTerm || 
            article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            article.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
          const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
          const matchesSport = selectedSport === 'all' || article.sport?.slug === selectedSport;
          return matchesSearch && matchesCategory && matchesSport;
        });
        
        setNews(filteredNews);
        setTotalPages(1);
        setTotalArticles(filteredNews.length);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews(sampleNews);
      setTotalPages(1);
      setTotalArticles(sampleNews.length);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedNews = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/news/featured`);
      
      if (response.ok) {
        const data = await response.json();
        setFeaturedNews(data.articles || []);
      } else {
        // Use sample featured news
        setFeaturedNews(sampleNews.filter(article => article.isFeatured));
      }
    } catch (error) {
      console.error('Error fetching featured news:', error);
      setFeaturedNews(sampleNews.filter(article => article.isFeatured));
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'sports': return 'bg-blue-100 text-blue-800';
      case 'announcements': return 'bg-green-100 text-green-800';
      case 'events': return 'bg-purple-100 text-purple-800';
      case 'academics': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchNews();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-primary py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="heading-lg text-white mb-4">Eagles News</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Stay up to date with the latest news, announcements, and achievements 
            from East End Prep athletics and school community.
          </p>
        </div>
      </section>

      {/* Featured News Section */}
      {featuredNews.length > 0 && (
        <section className="bg-white py-12 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-md text-gray-900 mb-8 text-center">Featured Stories</h2>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredNews.slice(0, 3).map((article) => (
                  <div key={article.id} className="bg-white rounded-xl shadow-sm border-2 border-blue-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all duration-300">
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={article.featuredImageSignedUrl || article.featuredImage || '/api/placeholder/600/400'}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200`}>
                          {article.category}
                        </span>
                        {article.sport && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            {article.sport.name}
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-semibold text-gray-800 mb-3 leading-tight">
                        <Link 
                          to={`/news/${article.slug}`}
                          className="hover:text-blue-600 transition-colors duration-200"
                        >
                          {article.title}
                        </Link>
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <div className="flex items-center space-x-4">
                          <span className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {formatDate(article.publishedAt)}
                          </span>
                          <span className="flex items-center">
                            <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {article.readingTime} min read
                          </span>
                        </div>
                        <span className="flex items-center">
                          <EyeIcon className="w-4 h-4 mr-1 text-gray-400" />
                          {article.viewCount}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </section>
      )}

      {/* Search and Filters */}
      <section className="bg-white py-8 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search news articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10 w-full"
                />
              </div>
            </form>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <select
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input text-sm"
              >
                {categories.map(category => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>

              <select
                value={selectedSport}
                onChange={(e) => {
                  setSelectedSport(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-input text-sm"
              >
                {sports.map(sport => (
                  <option key={sport.value} value={sport.value}>
                    {sport.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 text-sm text-gray-600">
            Showing {news.length} of {totalArticles} articles
          </div>
        </div>
      </section>

      {/* News Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner spinner-primary w-8 h-8"></div>
            </div>
          ) : news.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {news.map((article) => (
                  <article key={article.id} className="bg-white rounded-xl shadow-sm border-2 border-blue-100 overflow-hidden hover:shadow-md hover:border-blue-200 transition-all duration-300">
                    <div className="aspect-w-16 aspect-h-9">
                      <img
                        src={article.featuredImageSignedUrl || article.featuredImage || '/api/placeholder/600/400'}
                        alt={article.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    <div className="p-6">
                      <div className="flex items-center space-x-2 mb-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          {article.category}
                        </span>
                        {article.sport && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                            {article.sport.name}
                          </span>
                        )}
                        {article.isFeatured && (
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">
                            Featured
                          </span>
                        )}
                      </div>
                      
                      <h3 className="text-xl font-semibold text-gray-800 mb-3 leading-tight">
                        <Link 
                          to={`/news/${article.slug}`}
                          className="hover:text-blue-600 transition-colors duration-200"
                        >
                          {article.title}
                        </Link>
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                        <div className="flex items-center space-x-3">
                          <span className="flex items-center">
                            <CalendarIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {formatDate(article.publishedAt)}
                          </span>
                          <span className="flex items-center">
                            <ClockIcon className="w-4 h-4 mr-1 text-gray-400" />
                            {article.readingTime} min
                          </span>
                        </div>
                        <span className="flex items-center">
                          <EyeIcon className="w-4 h-4 mr-1 text-gray-400" />
                          {article.viewCount}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm text-gray-500">
                          By {article.author.firstName} {article.author.lastName}
                        </span>
                        
                        <Link
                          to={`/news/${article.slug}`}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors duration-200 flex items-center"
                        >
                          Read More 
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-12">
                  <nav className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 rounded-md border ${
                          currentPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                  </nav>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <TagIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Articles Found</h3>
              <p className="text-gray-500">Try adjusting your search terms or filters to find more articles.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NewsPage;